import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Checkbox } from '../ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { AssetPicker } from '../AssetPicker'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog'
import { CloseStatusBadge, useCloseIssues } from './CloseStatusBadge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { IssueBody } from '../issues/IssueBody'
import { IssueTargetLink } from '../issues/IssueTargetLink'
import { DismissSuggestionDialog } from '../issues/DismissSuggestionDialog'
import { type SuggestionIssue, suggestionLabel, DeadlineChip } from '../issues/suggestionKit'
import { useTicketSubtypes } from '../../hooks/useTicketSubtypes'
import { formatIssueReference } from '../../lib/issue-reference'
import { SUBCATEGORIES_BY_CATEGORY, subcategoryLabelKey } from '../steering/steering-categories'
import { EmptyState } from '../ui/empty-state'
import { Badge } from '../ui/badge'
import { followUpDue, proofLabel } from '../issues/issueFamily'
import { mdiBellOffOutline, mdiDomain, mdiChevronLeft, mdiChevronRight, mdiClipboardAlertOutline, mdiInboxOutline, mdiLoading, mdiMagnify } from '@mdi/js'

/**
 * The ticket queue: a filter bar, the subcategory facet, a server-paginated
 * master list and the selected ticket's detail.
 *
 * It is shared rather than duplicated because two pages now show a queue over
 * the same tickets, narrowed to different categories — Steering (economics /
 * incidents / compliance) and Data quality, which moved to the Data section
 * because data stewardship is not the property manager's work
 * (spec 2026-08-24-03, lot 10). Everything that differs between the two hosts
 * is a prop; the queue itself knows only "one category, its facet, its rows".
 *
 * The queue owns its two queries and nothing else. URL state stays with the
 * host, which is what lets each page keep its own search-param contract while
 * the rendering stays identical.
 */

/** A ticket as the queue consumes it (issues of every diagnostic type). */
type Ticket = SuggestionIssue & {
    /** The ticket's short reference, sequential per organization. */
    number?: number
    status: string
    priority: number
    type?: string | null
    created_at?: string | null
    nb_comments?: number | null
    follow_up_at?: string | null
    /** A dossier's progress and current step (spec 2026-10-27-19 § 6). */
    children_summary?: {
        total: number
        closed: number
        current_step?: { uid: string; title: string; status: string; due_date?: string | null; waiting_for?: Record<string, unknown> | null } | null
    } | null
}

/** The scenario a dossier runs (spec 2026-10-28-15 § 10), from its properties. */
type ScenarioRunProps = { code?: string; label?: Record<string, string> }
function scenarioRunOf(s: { properties?: unknown }): ScenarioRunProps | null {
    const run = (s.properties as { scenario_run?: ScenarioRunProps } | null | undefined)?.scenario_run
    return run?.code ? run : null
}
function scenarioRunLabel(run: ScenarioRunProps | null, lang: string): string {
    if (!run) return ''
    return run.label?.[lang.slice(0, 2)] || run.label?.en || run.code || ''
}

/** One page of the queue. The API caps `limit` at 100 and silently falls back
 * to 20 above it — 50 keeps a comfortable margin and a readable pager. */
export const PAGE_SIZE = 50

export const DEFAULT_STATUS = 'draft,open,in_progress'

const PRIORITY_BAR: Record<number, string> = {
    1: 'bg-red-500',
    2: 'bg-orange-500',
    3: 'bg-yellow-500',
    4: 'bg-blue-500',
    5: 'bg-muted-foreground/50',
}

export type TicketQueueState = {
    subcategory?: string
    status: string
    sort: string
    q: string
    page: number
    selected: string | null
    /** Show the sub-tickets as rows (`?children=1`). Off, the queue lists
     * dossiers only and a dossier shows its current step (spec 2026-10-27-19). */
    children?: boolean
    /** `due` keeps the tickets whose follow-up date has passed. */
    followUp?: string
}

function relativeDays(iso: string | null | undefined, t: (k: string, o?: Record<string, unknown>) => string): string {
    if (!iso) return ''
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
    if (days <= 0) return t('steering.today')
    return t('steering.days_ago', { count: days })
}

/** Turn a `group_by` stats response into a {value: count} index. */
export function toCounts(rows: Array<Record<string, unknown>> | undefined, key: string): Record<string, number> {
    const out: Record<string, number> = {}
    for (const row of rows ?? []) {
        const value = row[key]
        if (typeof value === 'string' && typeof row.count === 'number') out[value] = row.count
    }
    return out
}

/** Due-date filter options → the `due_after` / `due_before` bounds, computed
 * from "now" on the client so the server stays stateless. A ticket without a
 * due date never matches any option but "any". */
export const DUE_OPTIONS = ['gt30', 'gt7', 'overdue', 'overdue7', 'overdue30'] as const

export function dueBounds(due: string | undefined, now = new Date()): { due_after?: string; due_before?: string } {
    const shifted = (days: number) => new Date(now.getTime() + days * 86_400_000).toISOString()
    switch (due) {
        case 'gt30': return { due_after: shifted(30) }
        case 'gt7': return { due_after: shifted(7) }
        case 'overdue': return { due_before: now.toISOString() }
        case 'overdue7': return { due_before: shifted(-7) }
        case 'overdue30': return { due_before: shifted(-30) }
        default: return {}
    }
}

/** The asset and due-date filters a host opts into. */
export type TicketQueueFilters = {
    /** Scoped asset uid (`?asset=`). */
    asset?: string
    /** Include the asset's sub-assets (default). Off sends `asset_uid`. */
    subtree: boolean
    due?: string
    /** The sidebar badge's list (spec 2026-10-27-23): the caller's attention
     * tickets with this instant as "last seen", across categories and steps,
     * as `GET /issues?attention_since=` counts them. */
    attention?: string
}

export function TicketQueue({
    org,
    category,
    state,
    categoryTotal,
    assetSubtreeUid,
    filters,
    onChange,
    onSelect,
    testIdPrefix = 'steering',
    toolbarExtra,
    onLoaded,
}: {
    org: string
    category: string
    state: TicketQueueState
    /** Total for the "All" pill. The host already counts categories, so it
     * passes the number rather than making the queue ask a second time. */
    categoryTotal?: number
    /** Narrow both the queue and its facet to one asset's branch. Subtree, not
     * exact match: a rolled-up count on the home page promises descendants, so
     * an exact filter would open a queue that contradicts it. */
    assetSubtreeUid?: string
    /** When given, the toolbar shows the asset picker, the sub-assets toggle
     * and the due-date filter, and they drive the queries (this takes over
     * from `assetSubtreeUid`). */
    filters?: TicketQueueFilters
    /** Partial URL-state update. The host owns the search params. */
    onChange: (next: Record<string, string | number | undefined>) => void
    onSelect: (uid: string | null) => void
    testIdPrefix?: string
    /** Host-specific controls pinned to the right of the filter bar. */
    toolbarExtra?: ReactNode
    /** Called once, after the first list response. */
    onLoaded?: () => void
}) {
    const { t, i18n } = useTranslation('issues')
    const { byCode } = useTicketSubtypes(org)
    const [dismissTarget, setDismissTarget] = useState<SuggestionIssue | null>(null)

    const { subcategory = '', status, sort, q, page, selected, children = false, followUp } = state
    // The queue lists dossiers, not their steps, unless asked: both the rows
    // and the pill counts go roots-only.
    const attention = filters?.attention
    const familyQuery = attention
        // The badge counts tickets, not dossiers: every step is a row here.
        ? { roots_only: false, follow_up: undefined }
        : { roots_only: !children, follow_up: followUp === 'due' ? ('due' as const) : undefined }

    const filterAsset = filters ? filters.asset : assetSubtreeUid
    const subtree = filters ? filters.subtree : true
    const due = filters?.due
    // Computed once per option, not per render: "now" in the query key would
    // refetch forever.
    const bounds = useMemo(() => dueBounds(due), [due])
    const scopeQuery = {
        asset_subtree_uid: filterAsset && subtree ? filterAsset : undefined,
        asset_uid: filterAsset && !subtree ? filterAsset : undefined,
        ...bounds,
    }

    // ---- Bulk selection. Cleared whenever the result set changes under it.
    const [picked, setPicked] = useState<Set<string>>(new Set())
    const [confirmOpen, setConfirmOpen] = useState(false)
    const resultKey = [category, subcategory, status, sort, q, page, filterAsset, subtree, due, children, followUp, attention].join('|')
    useEffect(() => {
        setPicked(new Set())
    }, [resultKey])
    const { close: closeMany, pending: closing } = useCloseIssues(org, () => {
        setPicked(new Set())
        setConfirmOpen(false)
    })

    // One pane at a time below `lg`: picking a ticket swaps the list out for
    // the detail, in place. The window keeps whatever scroll offset the list
    // had, so a ticket picked halfway down the queue opened on the top of the
    // steering page instead of on the ticket (#1350). Bring the detail into
    // view ourselves — only below the breakpoint the swap happens at, since on
    // desktop both panes are on screen and a scroll would be gratuitous.
    // Deferred by two frames on purpose: selecting a ticket is a navigation,
    // and the router's own scroll restoration runs after this effect — a
    // scroll issued synchronously here is undone a frame later.
    const detailRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        if (!selected) return
        if (typeof window === 'undefined' || !window.matchMedia('(max-width: 1023px)').matches) return
        let inner = 0
        const outer = requestAnimationFrame(() => {
            inner = requestAnimationFrame(() => {
                detailRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
            })
        })

        return () => {
            cancelAnimationFrame(outer)
            cancelAnimationFrame(inner)
        }
    }, [selected])

    // The in-category facet. Counted with `category` applied, so a pill always
    // reads "this subcategory, inside this category" — the two axes are ANDed,
    // never derived from one another.
    const subcategoryStats = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues/stats', {
        params: {
            path: { orgCode: org },
            query: { group_by: 'subcategory', status, category, assignee_uid: 'all', ...scopeQuery, ...familyQuery },
        },
    })

    const issuesQuery = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues', {
        params: {
            path: { orgCode: org },
            query: {
                // The attention list is the badge's predicate: no category,
                // no subcategory, its own statuses (spec 2026-10-27-23).
                category: attention ? undefined : category,
                subcategory: attention ? undefined : subcategory || undefined,
                attention_since: attention,
                ...scopeQuery,
                status: attention ? undefined : status || undefined,
                assignee_uid: 'all',
                q: q || undefined,
                sort,
                ...familyQuery,
                page,
                limit: PAGE_SIZE,
            } as unknown as Record<string, unknown>,
        },
    })

    const countsBySubcategory = useMemo(() => toCounts(subcategoryStats.data?.data, 'subcategory'), [subcategoryStats.data])

    const loadedOnce = useRef(false)
    useEffect(() => {
        if (issuesQuery.isSuccess && !loadedOnce.current) {
            loadedOnce.current = true
            onLoaded?.()
        }
    }, [issuesQuery.isSuccess, onLoaded])

    const queue = (issuesQuery.data?.data ?? []) as Ticket[]
    const total = (issuesQuery.data as { total?: number } | undefined)?.total ?? 0
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
    const loading = issuesQuery.isLoading
    const selectable = queue.filter((s) => s.status !== 'closed')
    const allPicked = selectable.length > 0 && selectable.every((s) => picked.has(s.uid))
    const togglePicked = (uid: string, on: boolean) =>
        setPicked((prev) => {
            const next = new Set(prev)
            if (on) next.add(uid)
            else next.delete(uid)
            return next
        })

    // Declaration order, never sorted by count: the order is editorial (the
    // Incidents pills follow the lifecycle reported → dispatched → claimed) and
    // a facet that reshuffles itself as counts move is unreadable.
    const subcategoryPills = useMemo(
        () =>
            (SUBCATEGORIES_BY_CATEGORY[category as keyof typeof SUBCATEGORIES_BY_CATEGORY] ?? [])
                .map((s) => [s, countsBySubcategory[s] ?? 0] as const)
                // Empty slots stay hidden — but the active filter stays on
                // screen even at zero, or the pill that emptied the queue
                // disappears and cannot be un-clicked.
                .filter(([s, n]) => n > 0 || s === subcategory),
        [category, countsBySubcategory, subcategory],
    )

    const allPillCount = categoryTotal ?? Object.values(countsBySubcategory).reduce((a, b) => a + b, 0)

    return (
        <>
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="relative flex-1 min-w-[180px] max-w-md">
                    <AppIcon path={mdiMagnify} size="xs" className="absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <Input
                        value={q}
                        onChange={(e) => onChange({ q: e.target.value || undefined })}
                        placeholder={t('steering.search_placeholder')}
                        className="pl-8 h-8"
                        data-testid={`${testIdPrefix}-search`}
                    />
                </div>
                <Select value={status} onValueChange={(v) => onChange({ status: v === DEFAULT_STATUS ? undefined : v })}>
                    <SelectTrigger className="h-8 text-xs w-[130px]" data-testid={`${testIdPrefix}-status-filter`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={DEFAULT_STATUS}>{t('steering.status_active')}</SelectItem>
                        <SelectItem value="open,in_progress">{t('steering.status_open')}</SelectItem>
                        <SelectItem value="closed">{t('steering.status_closed')}</SelectItem>
                        <SelectItem value="draft,open,in_progress,closed">{t('steering.status_all')}</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={sort} onValueChange={(v) => onChange({ sort: v === 'priority' ? undefined : v })}>
                    <SelectTrigger className="h-8 text-xs w-[130px]" data-testid={`${testIdPrefix}-sort`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="priority">{t('steering.sort_priority')}</SelectItem>
                        <SelectItem value="due">{t('steering.sort_due')}</SelectItem>
                        <SelectItem value="recent">{t('steering.sort_recent')}</SelectItem>
                    </SelectContent>
                </Select>
                {filters && (
                    <>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant={filters.asset ? 'secondary' : 'outline'}
                                    size="sm"
                                    className="h-8 text-xs gap-1"
                                    data-testid={`${testIdPrefix}-asset-filter`}
                                >
                                    <AppIcon path={mdiDomain} size="xs" />
                                    {t(filters.asset ? 'steering.asset_filter_active' : 'steering.asset_filter')}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-80">
                                <AssetPicker
                                    org={org}
                                    value={filters.asset}
                                    testId={`${testIdPrefix}-asset-filter-picker`}
                                    onChange={(uid) => onChange({ asset: uid })}
                                />
                            </PopoverContent>
                        </Popover>
                        {filters.asset && (
                            <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Checkbox
                                    checked={filters.subtree}
                                    onCheckedChange={(on) => onChange({ subtree: on ? undefined : 0 })}
                                    data-testid={`${testIdPrefix}-subtree-toggle`}
                                />
                                {t('steering.include_subassets')}
                            </label>
                        )}
                        <Select value={due ?? 'any'} onValueChange={(v) => onChange({ due: v === 'any' ? undefined : v })}>
                            <SelectTrigger className="h-8 text-xs w-[190px]" data-testid={`${testIdPrefix}-due-filter`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">{t('steering.due_any')}</SelectItem>
                                {DUE_OPTIONS.map((o) => (
                                    <SelectItem key={o} value={o}>{t(`steering.due_${o}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                )}
                <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Checkbox
                        checked={followUp === 'due'}
                        onCheckedChange={(on) => onChange({ follow_up: on ? 'due' : undefined })}
                        data-testid="ticket-queue-follow-up-due"
                    />
                    {t('steering.follow_up_due')}
                </label>
                <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Checkbox
                        checked={children}
                        onCheckedChange={(on) => onChange({ children: on ? 1 : undefined })}
                        data-testid="ticket-queue-show-children"
                    />
                    {t('steering.show_children')}
                </label>
                {toolbarExtra}
            </div>

            {/* Bulk bar — appears as soon as a row is ticked. */}
            {picked.size > 0 && (
                <div className="flex items-center gap-2 mb-2 rounded-md border bg-muted/40 px-3 py-1.5" data-testid={`${testIdPrefix}-bulk-bar`}>
                    <span className="text-xs text-foreground" data-testid={`${testIdPrefix}-selected-count`}>
                        {t('steering.n_selected', { count: picked.size })}
                    </span>
                    <Button
                        size="sm"
                        className="h-7 ml-auto"
                        disabled={closing}
                        onClick={() => setConfirmOpen(true)}
                        data-testid={`${testIdPrefix}-bulk-close`}
                    >
                        {t('steering.close')}
                    </Button>
                </div>
            )}

            {/* Subcategory pills — contained in the active category by
                construction, counted server side. */}
            {!attention && subcategoryPills.length > 0 && (
                <div className="flex gap-1 flex-wrap mb-3" data-testid={`${testIdPrefix}-subcategories`}>
                    <button
                        onClick={() => onChange({ subcategory: undefined })}
                        className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${!subcategory ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-secondary'}`}
                        data-testid={`${testIdPrefix}-subcategory-all`}
                    >
                        {t('steering.subcategory_all')} ({allPillCount.toLocaleString()})
                    </button>
                    {subcategoryPills.map(([s, n]) => (
                        <button
                            key={s}
                            onClick={() => onChange({ subcategory: subcategory === s ? undefined : s })}
                            className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${subcategory === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-secondary'}`}
                            data-testid={`${testIdPrefix}-subcategory-${s}`}
                        >
                            {t(subcategoryLabelKey(s))} ({n.toLocaleString()})
                        </button>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,440px)_1fr] gap-4 items-start">
                {/* Master — the list. On mobile it yields the screen to the
                    detail once a ticket is picked (one pane at a time). */}
                <div className={`border rounded-lg overflow-hidden flex-col min-h-[60vh] ${selected ? 'hidden lg:flex' : 'flex'}`}>
                    {!loading && selectable.length > 0 && (
                        <label className="flex items-center gap-2 px-3 py-1.5 border-b bg-muted/30 text-[11px] text-muted-foreground">
                            <Checkbox
                                checked={allPicked}
                                onCheckedChange={(on) => setPicked(on ? new Set(selectable.map((s) => s.uid)) : new Set())}
                                data-testid={`${testIdPrefix}-select-all`}
                            />
                            {t('steering.select_all')}
                        </label>
                    )}
                    <div className="flex-1 overflow-y-auto divide-y" data-testid={`${testIdPrefix}-queue`}>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
                            </div>
                        ) : queue.length === 0 ? (
                            <EmptyState
                                variant="inline"
                                live
                                className="py-10"
                                icon={mdiClipboardAlertOutline}
                                title={t('steering.queue_empty')}
                                data-testid={`${testIdPrefix}-queue-empty`}
                            />
                        ) : (
                            queue.map((s) => {
                                const rule = suggestionLabel(s, byCode, i18n.language)
                                const isSel = selected === s.uid
                                // The bell mutes a ticket (close + snooze so it stops
                                // alerting) — available on any ticket that isn't
                                // already closed.
                                const canDismiss = s.status !== 'closed'
                                // A dossier reads through its current step: its
                                // due date and overdue state are the step's.
                                const family = s.children_summary
                                const step = family?.current_step
                                const rowDue = step?.due_date ?? s.due_date
                                return (
                                    // A div with role="button" rather than a real
                                    // <button>: the row hosts the target pill (an
                                    // anchor) and the dismiss control, and interactive
                                    // content nested inside a <button> is invalid HTML.
                                    <div
                                        key={s.uid}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => onSelect(s.uid)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                onSelect(s.uid)
                                            }
                                        }}
                                        className={`w-full text-left flex items-start gap-2.5 px-3 py-2.5 transition-colors cursor-pointer ${isSel ? 'bg-primary/10' : 'hover:bg-muted/50'}`}
                                        data-testid={`${testIdPrefix}-row-${s.uid}`}
                                    >
                                        {canDismiss && (
                                            <Checkbox
                                                className="mt-0.5 flex-shrink-0"
                                                checked={picked.has(s.uid)}
                                                aria-label={t('steering.select_row')}
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                onCheckedChange={(on) => togglePicked(s.uid, on)}
                                                data-testid={`${testIdPrefix}-select-${s.uid}`}
                                            />
                                        )}
                                        <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${PRIORITY_BAR[s.priority] ?? 'bg-muted'}`} />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-foreground truncate">
                                                {/* The reference first: it is what a resident
                                                    quotes back and what the search box matches. */}
                                                <span
                                                    className="text-muted-foreground tabular-nums mr-1.5"
                                                    data-testid={`ticket-number-${s.uid}`}
                                                >
                                                    {formatIssueReference(s.number, i18n.language)}
                                                </span>
                                                {s.title}
                                            </div>
                                            {step && (
                                                <div className="text-[11px] text-muted-foreground truncate" data-testid={`ticket-current-step-${s.uid}`}>
                                                    <span data-testid="ticket-current-step">
                                                        {t('steering.current_step', { title: step.title })}
                                                        {proofLabel(t, step.waiting_for) && ` · ${t('steering.current_step_waiting', { proof: proofLabel(t, step.waiting_for) })}`}
                                                        {step.due_date && ` · ${t('steering.current_step_due', { date: new Date(step.due_date).toLocaleDateString(i18n.language) })}`}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2 mt-0.5 min-w-0">
                                                {rule && <span className="text-[11px] text-muted-foreground truncate">{rule}</span>}
                                                {s.target_entity_type === 'asset' && s.target_entity_uid && (
                                                    <span className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                                        <IssueTargetLink orgCode={org} type="asset" uid={s.target_entity_uid} compact />
                                                    </span>
                                                )}
                                                <span className="text-[11px] text-muted-foreground/70 ml-auto flex-shrink-0">{relativeDays(s.created_at, t)}</span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                            {rowDue && <DeadlineChip dueDate={rowDue} />}
                                            {scenarioRunOf(s) && (
                                                <Badge variant="outline" className="text-[11px] max-w-[12rem] truncate" data-testid={`ticket-scenario-chip-${s.uid}`}>
                                                    <span data-testid="ticket-scenario-chip">{t('scenarios:chip', { label: scenarioRunLabel(scenarioRunOf(s), i18n.language) })}</span>
                                                </Badge>
                                            )}
                                            {family && family.total > 0 && (
                                                <Badge variant="outline" className="text-[11px] tabular-nums" data-testid={`ticket-children-count-${s.uid}`}>
                                                    <span data-testid="ticket-children-count">{family.closed} / {family.total}</span>
                                                </Badge>
                                            )}
                                            {followUpDue(s.follow_up_at) && s.status !== 'closed' && (
                                                <Badge variant="outline" className="text-[11px] border-warning text-warning" data-testid={`ticket-follow-up-due-${s.uid}`}>
                                                    {t('follow_up.due')}
                                                </Badge>
                                            )}
                                            <div className="flex items-center gap-2">
                                            <CloseStatusBadge
                                                org={org}
                                                uid={s.uid}
                                                status={s.status}
                                                stopPropagation
                                                testId={`${testIdPrefix}-close-${s.uid}`}
                                            />
                                            {canDismiss && (
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    className="text-muted-foreground hover:text-foreground p-1 -m-1"
                                                    aria-label={t('suggestion.dismiss')}
                                                    title={t('suggestion.dismiss')}
                                                    onClick={(e) => { e.stopPropagation(); setDismissTarget(s) }}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setDismissTarget(s) } }}
                                                    data-testid={`${testIdPrefix}-dismiss-${s.uid}`}
                                                >
                                                    <AppIcon path={mdiBellOffOutline} size="xs" />
                                                </span>
                                            )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>

                    {/* Pager — the queue is server-paginated, so the range and
                        the total are the server's, not the page's. */}
                    {!loading && total > 0 && (
                        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t bg-muted/30" data-testid={`${testIdPrefix}-pager`}>
                            <span className="text-[11px] text-muted-foreground tabular-nums" data-testid={`${testIdPrefix}-range`}>
                                {t('steering.range', {
                                    from: (page - 1) * PAGE_SIZE + 1,
                                    to: Math.min(page * PAGE_SIZE, total),
                                    total,
                                })}
                            </span>
                            {totalPages > 1 && (
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        disabled={page <= 1}
                                        aria-label={t('steering.previous_page')}
                                        title={t('steering.previous_page')}
                                        onClick={() => onChange({ page: page - 1 })}
                                        data-testid={`${testIdPrefix}-prev-page`}
                                    >
                                        <AppIcon path={mdiChevronLeft} size="sm" tone="muted" />
                                    </Button>
                                    <span className="text-[11px] text-muted-foreground tabular-nums">
                                        {page} / {totalPages}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        disabled={page >= totalPages}
                                        aria-label={t('steering.next_page')}
                                        title={t('steering.next_page')}
                                        onClick={() => onChange({ page: page + 1 })}
                                        data-testid={`${testIdPrefix}-next-page`}
                                    >
                                        <AppIcon path={mdiChevronRight} size="sm" tone="muted" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Detail — the selected ticket. Hidden on mobile until a
                    ticket is picked; the empty state is desktop-only. */}
                <div ref={detailRef} className={`border rounded-lg min-h-[60vh] ${selected ? 'block' : 'hidden lg:block'}`}>
                    {selected ? (
                        <div className="p-4">
                            <button
                                className="lg:hidden inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
                                onClick={() => onSelect(null)}
                                data-testid={`${testIdPrefix}-back`}
                            >
                                <AppIcon path={mdiChevronLeft} size="sm" tone="muted" />
                                {t('steering.back_to_list')}
                            </button>
                            <IssueBody orgCode={org} issueUid={selected} compact />
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full min-h-[40vh] text-muted-foreground gap-2 p-8" data-testid={`${testIdPrefix}-detail-empty`}>
                            <AppIcon path={mdiInboxOutline} size="2xl" className="opacity-40" />
                            <p className="text-sm">{t('steering.select_hint')}</p>
                        </div>
                    )}
                </div>
            </div>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid={`${testIdPrefix}-bulk-confirm`}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('steering.bulk_close_title', { count: picked.size })}</AlertDialogTitle>
                        <AlertDialogDescription>{t('steering.bulk_close_description', { count: picked.size })}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={closing}>{t('common:actions.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={closing}
                            onClick={(e) => {
                                e.preventDefault()
                                closeMany([...picked])
                            }}
                            data-testid={`${testIdPrefix}-bulk-confirm-button`}
                        >
                            {t('steering.close')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <DismissSuggestionDialog
                orgCode={org}
                suggestion={dismissTarget}
                onOpenChange={(o) => { if (!o) setDismissTarget(null) }}
            />
        </>
    )
}
